"use client"

import Link from "next/link"
import { Smartphone, X, ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { useMobileInstall } from "@/hooks/use-mobile-install"

type Variant = "sidebar" | "sidebar-skinny" | "navbar-chip"

interface MobileInstallCardProps {
  variant: Variant
  className?: string
}

const TARGET = "/dashboard-cms/app"

export default function MobileInstallCard({ variant, className }: MobileInstallCardProps) {
  const { isInstalled, isDismissed, dismiss } = useMobileInstall()

  if (isInstalled) return null
  if (isDismissed) return null

  if (variant === "navbar-chip") {
    return (
      <Link
        href={TARGET}
        aria-label="App mobile (novidade)"
        className={cn(
          "group inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--brand-amber)]/40 px-2.5 text-[var(--brand-amber)] transition-colors hover:border-[var(--brand-amber)]/65 hover:bg-[var(--brand-amber)]/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40",
          className,
        )}
      >
        <Smartphone className="h-3.5 w-3.5" strokeWidth={2.2} />
        <span className="hidden text-[11.5px] font-semibold sm:inline">App mobile</span>
        <span className="hidden font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--brand-amber)]/65 sm:inline">
          novo
        </span>
      </Link>
    )
  }

  if (variant === "sidebar-skinny") {
    return (
      <Link
        href={TARGET}
        aria-label="App mobile, novidade"
        title="App mobile (novidade)"
        className={cn(
          "group relative mx-1 my-2 flex h-9 items-center justify-center rounded-md border border-[var(--brand-amber)]/30 bg-[var(--brand-amber)]/8 text-[var(--brand-amber)] transition-colors hover:bg-[var(--brand-amber)]/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40",
          className,
        )}
      >
        <Smartphone className="h-4 w-4" />
        <span className="absolute right-1 top-1 flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--brand-amber)] opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--brand-amber)]" />
        </span>
      </Link>
    )
  }

  return (
    <div
      className={cn(
        "relative mx-2 mt-3 overflow-hidden rounded-xl border border-[var(--brand-amber)]/25 bg-[var(--brand-amber)]/[0.06] p-3.5",
        className,
      )}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dispensar aviso de app mobile"
        className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
      >
        <X className="h-3 w-3" />
      </button>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--brand-amber)]">
          novidade
        </span>
        <span className="flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-1.5 w-1.5 animate-ping rounded-full bg-[var(--brand-amber)] opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--brand-amber)]" />
        </span>
      </div>

      <p className="mt-1 font-display text-[15px] italic text-foreground">App no celular</p>
      <p className="mt-1 pr-4 text-[11.5px] leading-snug text-muted-foreground">
        Instale o painel como aplicativo. Atalho na tela inicial.
      </p>

      <Link
        href={TARGET}
        className="mt-2.5 inline-flex h-8 items-center gap-1 rounded-md bg-foreground px-2.5 text-[11.5px] font-semibold text-background transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
      >
        <Smartphone className="h-3 w-3" /> Instalar
        <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  )
}
